// Guided walkthrough orchestration — provider, welcome dialog, and the module
// sequencer that fires the right tour for whatever surface the user is on.
//
// Everything is gated behind the org-level `guided_tutorial` feature flag.
// Flag off (the default): no welcome dialog, no tours, no Settings section —
// zero behavior change. Rollout is managed from SuperAdmin → Feature Flags.
//
// A module plays only as part of a walkthrough record (Drew, 2026-10-01: one
// tour, all mock data, nothing typed): the full tour the welcome dialog or
// Settings' Restart All starts, or one section started from Settings →
// Walkthroughs. The record lives in sessionStorage so it survives the route
// change to Downline Management, which mounts its own provider. The progress
// row only remembers whether the welcome dialog was answered.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Driver } from 'driver.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import {
  candidateModulesFor,
  fullTourFor,
  walkthroughVisible,
  stepsWithHandoff,
  walkthroughById,
  WALKTHROUGHS,
  WELCOME_MODULE,
  type TourModule,
  type Walkthrough,
} from './content';
import { runTour } from './engine';
import {
  EMPTY_PROGRESS,
  loadActiveWalkthrough,
  loadProgress,
  saveActiveWalkthrough,
  saveProgress,
  type ActiveWalkthrough,
  type TutorialProgress,
} from './storage';

export const TUTORIAL_FLAG = 'guided_tutorial';

interface TutorialContextValue {
  /** True when the feature flag is on for this user's org. */
  enabled: boolean;
  /** Replay one walkthrough from its first surface (Settings → Walkthroughs → Start). */
  startWalkthrough: (id: string) => void;
  /** Play every walkthrough this person can see, in order, as one flow (Restart All, the welcome dialog). */
  startFullTour: () => void;
  /** End whatever walkthrough is playing, right now (the banners' Exit buttons). */
  endWalkthrough: () => void;
  /** The walkthrough that is still playing, if any. */
  activeWalkthroughId: string | null;
}

const TutorialContext = createContext<TutorialContextValue>({
  enabled: false,
  startWalkthrough: () => {},
  startFullTour: () => {},
  endWalkthrough: () => {},
  activeWalkthroughId: null,
});

/** Safe anywhere — returns the disabled default outside a provider (tests, storybook-style renders). */
export function useTutorial(): TutorialContextValue {
  return useContext(TutorialContext);
}

interface TutorialProviderProps {
  /** Current app view (subset of AppView the tutorial cares about). */
  view: string;
  wizardStep: number;
  /**
   * The walkthrough ids this person may see, in full-tour order. The welcome
   * dialog's Start practice run and Settings' Restart All play these as one
   * flow. Defaults to every walkthrough.
   */
  fullTourIds?: readonly string[];
  /**
   * Called after Settings → Walkthroughs → Start records the walkthrough. The
   * host gets the person onto its first surface (practice mode, My Clients, a
   * comparison, Downline Management); the sequencer takes it from there.
   */
  onStartWalkthrough?: (walkthrough: Walkthrough) => void;
  /** Called when a module has played (or was closed) — the host moves the person
   *  on where the tour can't: the results module ends by opening the comparison. */
  onModuleEnd?: (moduleId: string) => void;
  /** Called when the person closes the "That's the tour" dialog at the end of the
   *  full tour; a host on another route takes them back to My Clients. */
  onTourFinished?: () => void;
  /** Called once a walkthrough has played its last module — the host clears any
   *  sample data it put up for it. `continuing` is true when the full tour goes
   *  straight on to the next section. */
  onWalkthroughEnd?: (walkthrough: Walkthrough, continuing: boolean) => void;
  children: React.ReactNode;
}

// The default when the host passes nothing: someone with a client book who can
// see Downline. Index always passes the real list for the person.
const ALL_WALKTHROUGH_IDS = fullTourFor(WALKTHROUGHS.filter(w => walkthroughVisible(w.requires, { canSeeDownline: true, noClientBook: false })));

export function TutorialProvider({ view, wizardStep, fullTourIds = ALL_WALKTHROUGH_IDS, onStartWalkthrough, onWalkthroughEnd, onModuleEnd, onTourFinished, children }: TutorialProviderProps) {
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const enabled = isEnabled(TUTORIAL_FLAG);
  // View-as runs on the admin's own session and progress, and every write is dropped, so a
  // welcome dialog there could never be answered for good. No welcome, no tours.
  const { isImpersonating } = useImpersonation();

  const [progress, setProgress] = useState<TutorialProgress | null>(null);
  const [active, setActive] = useState<ActiveWalkthrough | null>(() => loadActiveWalkthrough());
  // The full tour just played its last section: say so, once.
  const [finished, setFinished] = useState(false);
  const activeDriverRef = useRef<Driver | null>(null);

  // A walkthrough ends when its record is cleared or replaced by the next one in
  // the chain. A record the chain reached (not a Start button) still needs the
  // host to put the person on its first surface, so that is done here too.
  const onEndRef = useRef(onWalkthroughEnd);
  onEndRef.current = onWalkthroughEnd;
  const onStartRef = useRef(onStartWalkthrough);
  onStartRef.current = onStartWalkthrough;
  const onModuleEndRef = useRef(onModuleEnd);
  onModuleEndRef.current = onModuleEnd;
  const activeRef = useRef(active);
  activeRef.current = active;
  const prevActiveIdRef = useRef<string | null>(active?.id ?? null);
  useEffect(() => {
    const prev = prevActiveIdRef.current;
    const nextId = active?.id ?? null;
    prevActiveIdRef.current = nextId;
    if (prev && prev !== nextId) {
      const wt = walkthroughById(prev);
      if (wt) onEndRef.current?.(wt, !!active?.fromChain);
    }
    if (active?.fromChain) {
      const wt = walkthroughById(active.id);
      const settled = { ...active };
      delete settled.fromChain;
      setActive(settled);
      saveActiveWalkthrough(settled);
      if (wt) onStartRef.current?.(wt);
    }
  }, [active]);

  // Load progress once we know who the user is (and only if the flag is on —
  // no reason to query for the 99% of sessions where the tutorial is off).
  useEffect(() => {
    if (!enabled || !user?.id) return;
    let cancelled = false;
    loadProgress(user.id).then(p => {
      if (!cancelled) setProgress(p);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, user?.id]);

  const persist = useCallback(
    (next: TutorialProgress) => {
      setProgress(next);
      if (user?.id) saveProgress(user.id, next);
    },
    [user?.id],
  );

  const persistActive = useCallback((next: ActiveWalkthrough | null) => {
    setActive(next);
    saveActiveWalkthrough(next);
  }, []);

  const markCompleted = useCallback(
    (moduleId: string) => {
      // The first-visit record, so a module played on request never replays on
      // its own later.
      setProgress(prev => {
        const base = prev ?? EMPTY_PROGRESS;
        if (base.completed.includes(moduleId)) return prev;
        const next = { ...base, completed: [...base.completed, moduleId] };
        if (user?.id) saveProgress(user.id, next);
        return next;
      });
      // The walkthrough being replayed, if any: done once every module played.
      setActive(prev => {
        if (!prev) return prev;
        const wt = walkthroughById(prev.id);
        if (!wt || !wt.modules.includes(moduleId) || prev.completed.includes(moduleId)) return prev;
        const completed = [...prev.completed, moduleId];
        let next: ActiveWalkthrough | null = { ...prev, completed };
        if (wt.modules.every(m => completed.includes(m))) {
          // Done. The full tour goes on to the next section; a single walkthrough ends.
          const [following, ...rest] = prev.chain ?? [];
          next = following ? { id: following, completed: [], chain: rest, fromChain: true } : null;
          // A record with a chain (even an empty one) is the full tour; its last
          // section ending is the moment for the closing dialog. A single Start
          // from Settings carries no chain and ends quietly.
          if (!next && Array.isArray(prev.chain)) setFinished(true);
        }
        saveActiveWalkthrough(next);
        return next;
      });
    },
    [user?.id],
  );

  const startWalkthrough = useCallback(
    (id: string, chain: string[] = []) => {
      const wt = walkthroughById(id);
      if (!wt) return;
      activeDriverRef.current?.destroy();
      activeDriverRef.current = null;
      persistActive({ id: wt.id, completed: [], ...(chain.length ? { chain } : {}) });
      onStartWalkthrough?.(wt);
    },
    [persistActive, onStartWalkthrough],
  );

  const fullTourKey = fullTourIds.join('|');
  const startFullTour = useCallback(() => {
    const [first, ...rest] = fullTourKey ? fullTourKey.split('|') : [];
    if (first) startWalkthrough(first, rest);
  }, [fullTourKey, startWalkthrough]);

  const endWalkthrough = useCallback(() => {
    activeDriverRef.current?.destroy();
    activeDriverRef.current = null;
    persistActive(null);
  }, [persistActive]);

  const completedSet = useMemo(
    () => new Set(progress?.completed ?? []),
    [progress?.completed],
  );

  const welcomeAnswered = completedSet.has(WELCOME_MODULE);
  const showWelcome =
    enabled && !isImpersonating && !active && !!progress && !progress.dismissed && !welcomeAnswered
    // My Clients, or All Clients for a support admin with no client book, whose
    // home it is (Drew, 2026-10-13: they still need to know how to work it).
    && (view === 'dashboard' || view === 'org-scenarios');
  // A tour plays only while a walkthrough record is active. The record is this
  // browser's session, so it works under View-as too (Drew, 2026-09-30, testing
  // them that way); the welcome dialog itself never shows there, since its
  // answer could not be saved.
  const touring = enabled && !!active;

  const acceptWelcome = () => {
    markCompleted(WELCOME_MODULE);
    startFullTour();
  };
  const skipWelcome = () => {
    const base = progress ?? EMPTY_PROGRESS;
    persist({
      completed: [...new Set([...base.completed, WELCOME_MODULE])],
      dismissed: true,
    });
  };

  // What counts as done, and which modules are in play: the walkthrough's own
  // sessionStorage record decides both, so a replay never depends on what the
  // DB row already lists.
  const activeId = active?.id ?? null;
  const activeCompletedKey = active?.completed.join('|') ?? '';
  const candidates = useMemo(() => {
    if (!activeId) return [] as TourModule[];
    const wt = walkthroughById(activeId);
    if (!wt) return [] as TourModule[];
    return candidateModulesFor(view, wizardStep, new Set(activeCompletedKey ? activeCompletedKey.split('|') : []), new Set(wt.modules));
  }, [activeId, activeCompletedKey, view, wizardStep]);

  // Module sequencer: whenever the surface changes (or a module completes),
  // start the first uncompleted module for this surface whose anchor exists.
  // A MutationObserver covers anchors that appear later — the strategy card
  // only exists after the advisor clicks "Add Scenario", the folder tour
  // waits for a folder, the Manage sheet tour for the sheet.
  useEffect(() => {
    if (!touring || candidates.length === 0) return;

    let cancelled = false;
    let startObserver: MutationObserver | null = null;
    let advanceObserver: MutationObserver | null = null;

    // Once the thing this module told the user to create actually exists,
    // end the module and let the sequencer move on. Without this the popup
    // sits over the new card, stale and mispositioned, until dismissed by
    // hand. Only armed when the target is absent at start, so a module never
    // skips itself over something that was already on screen.
    const armAdvance = (mod: TourModule) => {
      const selector = mod.advanceWhen;
      if (!selector || document.querySelector(selector)) return;
      const check = () => {
        if (cancelled || !document.querySelector(selector)) return;
        advanceObserver?.disconnect();
        advanceObserver = null;
        activeDriverRef.current?.destroy();
      };
      advanceObserver = new MutationObserver(check);
      advanceObserver.observe(document.body, { childList: true, subtree: true });
    };

    const tryStart = (): boolean => {
      if (cancelled || activeDriverRef.current) return false;
      const mod = candidates.find(m => {
        const readySelector = m.waitFor ?? m.steps[0]?.target;
        return !readySelector || !!document.querySelector(readySelector);
      });
      if (!mod) return false;
      startObserver?.disconnect();
      startObserver = null;
      activeDriverRef.current = runTour(stepsWithHandoff(mod, activeRef.current), () => {
        activeDriverRef.current = null;
        markCompleted(mod.id);
        onModuleEndRef.current?.(mod.id);
      });
      armAdvance(mod);
      return true;
    };

    // Small delay lets the view finish rendering before we spotlight it.
    const timer = setTimeout(() => {
      if (tryStart()) return;
      startObserver = new MutationObserver(() => tryStart());
      startObserver.observe(document.body, { childList: true, subtree: true });
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      startObserver?.disconnect();
      advanceObserver?.disconnect();
      // Navigating away ends the active tour; driver's onDestroyed marks the
      // module seen so it doesn't replay on every return visit.
      activeDriverRef.current?.destroy();
    };
  }, [touring, candidates, markCompleted]);

  const activeWalkthroughId = active?.id ?? null;
  const value = useMemo(
    () => ({ enabled, startWalkthrough, startFullTour, endWalkthrough, activeWalkthroughId }),
    [enabled, startWalkthrough, startFullTour, endWalkthrough, activeWalkthroughId],
  );
  const closeFinished = () => {
    setFinished(false);
    onTourFinished?.();
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
      <Dialog open={showWelcome} onOpenChange={open => { if (!open) skipWelcome(); }}>
        <DialogContent className="sm:max-w-md" data-tour="welcome-dialog">
          <DialogHeader>
            <DialogTitle>Welcome to Tactical Tax Genie</DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              Tactical Tax Genie models a client&apos;s current tax position, layers
              strategies on top, and compares the outcomes side by side. The tour
              walks through a sample client from start to finish, every page
              completed and calculated. Nothing to enter and nothing saved.
              <span className="block">It can be replayed at any time from Settings → Walkthroughs.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={skipWelcome}>
              Don&apos;t show again
            </Button>
            <Button onClick={acceptWelcome}>Start the tour</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={finished} onOpenChange={open => { if (!open) closeFinished(); }}>
        <DialogContent className="sm:max-w-md" data-tour="finished-dialog">
          <DialogHeader>
            <DialogTitle>Tour complete</DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              Welcome to Tactical Tax Genie. The sample data has been removed.
              Create your first client from My Clients. Any section of the tour
              can be replayed from Settings → Walkthroughs.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={closeFinished}>Get started</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TutorialContext.Provider>
  );
}
